import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { useAccount, State, Selectors } from '@grandvisionhq/auth';
import { MediaQuery, Button, Modal, Util } from '@grandvisionhq/common';
import { Container, Text } from '@grandvisionhq/elements';
import { Util as Util$1 } from '@grandvisionhq/forms';
import { Alert } from '@grandvisionhq/iris-ui';
import { parseHtml } from '@grandvisionhq/lib-utils-html';
import { useIntl, useSelector } from '@grandvisionhq/state';
import { pushNewsletterEvent } from '../../data-layer/newsletter.js';
import { Newsletter } from './Newsletter.js';

const NewsletterContainer = ({ alignment ='left' , className , contentAfterSubmit , contentBeforeSubmit , hasError , hasSubscribed , image , isLoading , mobileImage , modalButtonTitle , title , variant , onSubmit ,  })=>{
    const { getLabel , getFormattedLabel  } = useIntl();
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isSubscribeWithEmailChecked, setIsSubscribeWithEmailChecked] = React.useState(false);
    const [showSubscribeWithEmailError, setShowSubscribeWithEmailError] = React.useState(false);
    const authState = useAccount();
    const isLoggedIn = authState === State.AuthState.LoggedIn;
    const auth = useSelector(Selectors.getAuth);
    const authEmail = isLoggedIn && auth.email ? auth.email : undefined;
    // Make sure we have the necessary content props
    if (!title || !contentBeforeSubmit) {
        return null;
    }
    // Make sure all variants have the required data to render
    const isModal = !!(variant === 'modal' && modalButtonTitle);
    const isBloomreachComponent = !!(variant === 'bloomreach-component' && image);
    if (!isModal && !isBloomreachComponent) {
        return null;
    }
    const imageUrl = isSmallScreen && mobileImage ? mobileImage.url : image === null || image === void 0 ? void 0 : image.url;
    const labels = {
        closeModal: getLabel('common.actions.close'),
        emailAddress: getLabel('fields.input.email.label'),
        emailAddressErrorMessage: getLabel('account.error.emailInvalid'),
        firstName: getLabel('checkout.contact.firstName'),
        firstNameErrorMessage: getLabel('checkout.contact.firstName.errorMessage'),
        lastName: getLabel('checkout.contact.lastName'),
        lastNameErrorMessage: getLabel('checkout.contact.lastName.errorMessage'),
        subscribeWithEmail: getFormattedLabel('common.newsletterSignUp.subscribeWithEmail', {
            labelData: {
                email: authEmail !== null && authEmail !== void 0 ? authEmail : ''
            },
            parseLink: true
        }),
        subscribeWithEmailError: getLabel('common.newsletterSignUp.subscribeWithEmailError'),
        generalErrorTitle: getLabel('common.newsletterSignUp.error.generalTitle'),
        generalErrorAlertTitle: getLabel('common.newsletterSignUp.error.generalAlertTitle'),
        generalErrorAlertMessage: getLabel('common.newsletterSignUp.error.generalAlertMessage'),
        successTitle: getLabel('common.newsletterSignUp.success.title'),
        successAlertTitle: getLabel('common.newsletterSignUp.success.alertTitle'),
        successAlertMessage: getLabel('common.newsletterSignUp.success.alertMessage'),
        signUp: getLabel('common.newsletterSignUp.signUp'),
        mandateText: getLabel('account.optIn.newsletter')
    };
    const handleSubmit = (formState)=>{
        const { email , firstName , lastName  } = Util$1.getValuesFromState(formState.formState);
        if (isLoggedIn) {
            setShowSubscribeWithEmailError(false);
            if (!isSubscribeWithEmailChecked) {
                setShowSubscribeWithEmailError(true);
                return;
            }
        }
        if (email && Util$1.validations.isEmailAddress(email)) {
            onSubmit({
                email,
                firstName: firstName !== null && firstName !== void 0 ? firstName : '',
                lastName: lastName !== null && lastName !== void 0 ? lastName : '',
                mandateText: labels.mandateText
            });
        }
    };
    const getTitle = ()=>{
        if (hasSubscribed) {
            return labels.successTitle;
        }
        if (hasError) {
            return labels.generalErrorTitle;
        }
        return title;
    };
    const getContent = ()=>{
        // When the user hasn't yet subscribed we always show the `contentBeforeSubmit`.
        if (!hasSubscribed) {
            return /*#__PURE__*/ React.createElement(Text, null, parseHtml(contentBeforeSubmit));
        }
        // When the user has successfully subscribed we need to render the `contentAfterSubmit` instead of the `contentBeforeSubmit`.
        if (contentAfterSubmit) {
            return /*#__PURE__*/ React.createElement(Text, null, parseHtml(contentAfterSubmit));
        }
        // `contentAfterSubmit` is an optional field in BR, so don't render any content when it's undefined
        return null;
    };
    const getAlert = ()=>{
        if (hasSubscribed) {
            return /*#__PURE__*/ React.createElement(Alert, {
                title: labels.successAlertTitle,
                variant: "success",
                className: "alert-m-default"
            }, labels.successAlertMessage);
        }
        if (hasError) {
            return /*#__PURE__*/ React.createElement(Alert, {
                title: labels.generalErrorAlertTitle,
                variant: "error",
                className: "alert-m-default"
            }, labels.generalErrorAlertMessage);
        }
        return null;
    };
    const onModalOpen = ()=>{
        setIsModalOpen(true);
        pushNewsletterEvent({
            eventName: 'newsletter_subscription_click',
            interactionType: 'subscription_button_click'
        });
    };
    const onModalClose = (optionClicked)=>{
        setIsModalOpen(false);
        pushNewsletterEvent({
            interactionType: 'subscription_close_click',
            optionClicked,
            eventName: 'newsletter_subscription_close'
        });
    };
    const onSubscribeWithEmailChange = ()=>{
        setIsSubscribeWithEmailChecked(!isSubscribeWithEmailChecked);
    };
    const sharedProps = {
        alert: getAlert(),
        content: getContent(),
        hideForm: hasSubscribed,
        isLoading,
        labels,
        variant,
        authEmail,
        isSubscribeWithEmailChecked,
        showSubscribeWithEmailError,
        onSubscribeWithEmailChange,
        onSubmit: handleSubmit
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('newsletter', className)
    }, isModal ? /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Button, {
        className: "newsletter__toggle",
        caption: modalButtonTitle,
        onClick: onModalOpen
    }), /*#__PURE__*/ React.createElement(Modal, {
        className: "newsletter__modal",
        isOpen: isModalOpen,
        onModalClose: ()=>onModalClose('cross')
        ,
        showIcon: true,
        title: getTitle()
    }, /*#__PURE__*/ React.createElement(Newsletter, swcHelpers.extends({}, sharedProps, {
        showCancelButton: true,
        onModalClose: ()=>onModalClose('button')
    })))) : null, isBloomreachComponent ? /*#__PURE__*/ React.createElement("div", {
        className: classnames('newsletter__section', `newsletter__section--align-${alignment}`),
        "data-t": "newsletter-background-image",
        style: Util.createCssImageSetStyle(imageUrl !== null && imageUrl !== void 0 ? imageUrl : '', isSmallScreen ? 1024 : 1920, {
            fit: 'scale'
        }, {
            extraPixelDensities: [
                2
            ],
            style: {
                backgroundPosition: 'top',
                backgroundSize: 'cover'
            }
        })
    }, /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement(Newsletter, swcHelpers.extends({}, sharedProps, {
        title: getTitle()
    })))) : null);
};

export { NewsletterContainer as default };
//# sourceMappingURL=NewsletterContainer.js.map
